import { Skeleton, cn } from '@h2hspark/ui';
import { Eye, Lock, Users } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';

import { goingCountLabel } from '../event-discovery/constants';

import { useAttendeeRoster } from './api';
import { PrivateAttendeesRow } from './attendee-card';
import {
  emptyRosterNotice,
  fullName,
  initialsOf,
  roleAtCompany,
  seeAllAttendeesLabel,
} from './constants';
import type { AttendeePreviewCard } from './types';

/**
 * `MEM-011` AC-3, `MEM-015` AC-6 — the Who's coming module, in the event page's sticky sidebar.
 *
 * ## Transcribed from `Web App.dc.html` §2.2, the sidebar
 *
 * **For a member who is going:** a heading row — `<h3>` *Who's coming* at `var(--text-md)` with a
 * note at the right — then a 12px-gapped column of 40px-avatar rows (name at
 * `700/var(--text-sm)`, role at `var(--text-xs)` in `--text-muted`, both ellipsed), the private
 * row with its lock, and a full-width outline button, *See all N attendees*, to the roster.
 *
 * **For anybody else:** the lock row — a 40px `--surface-sunken` circle with `lock` in
 * `--text-subtle`, *Who's coming* at `700/var(--text-sm)`, and a line beneath at `var(--text-xs)`.
 *
 * ## Who sees the list (Sumit's ruling of 2026-09-11)
 *
 * *"Who is coming should only show if you are signed up for an event. and it should follow other
 * event and privacy setting around who is coming."* — `MEM-011` AC-1 and AC-3, `MEM-015` AC-6, a
 * confirmed departure from `MEM-DIS-014`. **The server enforces it**: the roster route answers a
 * signed-in member with no confirmed registration with the going count and a `registrants-only`
 * closure and nobody in it, so this component cannot show a stranger a list it was never sent. It
 * draws the server's sentence — *"Reserve your spot to see who's coming."* — where the list would
 * be. The event's own switch and the 72h window close it for a registrant too, with their own
 * sentences, and each attendee's election (a) decides who is in it.
 *
 * A **signed-out** visitor gets no count and a sign-in prompt (`MEM-019` AC-2): the roster route
 * is behind a session, so nothing is asked.
 *
 * ## Where the spec and the prototype differ, and which wins
 *
 * The prototype's registered sidebar heads the list with *"Opt-in"* and no count; the spec heads
 * the module with the going count and *"Opt-in preview"* (`MEM-DIS-014`, `MEM-015` AC-6), so both
 * are here, in the spec's words. The prototype's rows carry a name and a role; `MEM-011` AC-6 says
 * a preview card carries the conversation starter, so each row carries it too, in the prototype's
 * own quote treatment from the Matches grid (§3.9).
 *
 * Every sentence a member reads here is served by the API or is the prototype's own label, so the
 * wording a test asserts and the wording a member sees are one string.
 */
const PREVIEW_LENGTH = 5;

export function WhosComingPanel({
  eventId,
  divided = false,
}: {
  readonly eventId: string;
  /** Under the Reserve action the prototype rules the row off (`border-top:1px solid
   * var(--divider); padding-top:16px`); at the top of a registrant's sidebar it stands alone. */
  readonly divided?: boolean;
}): React.ReactElement {
  const { session } = useMemberSession();
  const signedIn = session !== null;
  const query = useAttendeeRoster(eventId, { enabled: signedIn });
  const roster = query.data?.roster;
  // Never a zero (`MEM-014`'s ruling of 2026-09-11, TW 44293976): "0 going" reads as *nobody is
  // coming* on an event that has simply not had a reservation yet. The cards and the hero pill
  // already follow it through `goingCountLabel`, so the heading reads the same answer.
  const going = goingCountLabel(roster?.goingCount);
  const rosterPath = `/app/events/${encodeURIComponent(eventId)}/attendees`;

  return (
    <section
      className={cn('space-y-3', divided ? 'border-t border-divider pt-4' : '')}
      aria-labelledby="whos-coming-heading"
      data-testid="whos-coming"
    >
      {/* Wraps rather than squeezes: in a 330px sidebar, or a 343px phone card, the note drops
          beneath the heading instead of breaking "Who's coming" over two lines. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex items-baseline gap-2 whitespace-nowrap">
          <h2 id="whos-coming-heading" className="text-md">
            Who&apos;s coming
          </h2>
          {going === null ? null : (
            <span className="font-brand text-sm font-bold text-muted-foreground">{going}</span>
          )}
        </div>
        <span className="inline-flex flex-none items-center gap-1.5 text-xs text-text-subtle">
          <Eye className="h-3.5 w-3.5" aria-hidden />
          Opt-in preview
        </span>
      </div>

      {!signedIn ? (
        // Not an empty list — an empty list under this heading says nobody is coming.
        <LockRow>
          <Link to="/app/login" className="font-bold text-brand underline underline-offset-2">
            Sign in
          </Link>{' '}
          to see who else is coming.
        </LockRow>
      ) : query.isPending ? (
        <div className="space-y-3" aria-hidden>
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : roster === undefined ? null : roster.closed !== null ? (
        <LockRow>{roster.closed.notice}</LockRow>
      ) : roster.attendees.length === 0 && roster.privateAttendees.count === 0 ? (
        /* **The heading, the count, and nothing under it** — reported by Sumit on 2026-09-11,
           seen on a past event and reachable on any event whose registrants have all kept their
           profiles private. `emptyRosterNotice` carries why it is a real state rather than only a
           rendering fault; a `<ul>` with no `<li>` in it is not an answer. */
        <p className="text-xs leading-snug text-muted-foreground" data-testid="whos-coming-empty">
          {emptyRosterNotice(roster.goingCount)}
        </p>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {roster.attendees.slice(0, PREVIEW_LENGTH).map((card) => (
              <li key={card.attendeeRef}>
                <AttendeeMiniRow
                  card={card}
                  to={`${rosterPath}/${encodeURIComponent(card.attendeeRef)}`}
                />
              </li>
            ))}
            {/* AC-4's private row is a sibling of the entries, so it is always last and can never
                be mistaken for one of them. It renders on the preview too: an event that looked
                like three people when fifteen more are private would be the dishonesty AC-5
                exists to prevent, arriving through the short list. */}
            <PrivateAttendeesRow privateAttendees={roster.privateAttendees} />
          </ul>

          {/* The prototype's *See all N attendees*, to the roster (`MEM-011` AC-4). N is the
              going count, the same figure the heading carries. A roster with nobody on it never
              reaches here — it is answered by the sentence above, which is also why a member who
              is the only one going is not offered a list with no one on it (the reader is never a
              row in their own roster). */}
          <Link
            to={rosterPath}
            className="flex w-full items-center justify-center gap-[7px] rounded-pill border-[1.5px] border-border bg-card px-4 py-[11px] font-brand text-sm font-bold text-primary transition-colors hover:border-primary"
          >
            <Users className="h-4 w-4" aria-hidden />
            {seeAllAttendeesLabel(roster.goingCount)}
          </Link>
        </>
      )}
    </section>
  );
}

/** The prototype's lock row (§3.23): a sunken circle with a lock, and the sentence beside it. */
function LockRow({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <p className="flex items-start gap-[11px]" data-testid="whos-coming-locked">
      <span
        className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-pill bg-muted text-text-subtle"
        aria-hidden
      >
        <Lock className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1 pt-2.5 text-xs leading-snug text-muted-foreground">
        {children}
      </span>
    </p>
  );
}

/**
 * The sidebar's attendee row — §3.9's *"lighter version"*: a 40px avatar, the name and the role,
 * ellipsed — with the conversation starter beneath (`MEM-011` AC-6). One link, for the reason
 * `AttendeeCard` gives: a real focus stop and a real name, where the prototype's `<div onClick>`
 * has neither.
 */
function AttendeeMiniRow({
  card,
  to,
}: {
  readonly card: AttendeePreviewCard;
  readonly to: string;
}): React.ReactElement {
  const line = roleAtCompany(card);
  return (
    <Link to={to} className="flex min-w-0 items-center gap-[11px] rounded-md hover:bg-muted/40">
      <span
        className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-pill bg-brand-100 font-brand text-sm font-bold text-brand-700"
        aria-hidden
      >
        {initialsOf(card)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-brand text-sm font-bold text-foreground">
          {fullName(card)}
        </span>
        {line === null ? null : (
          <span className="block truncate text-xs text-muted-foreground">{line}</span>
        )}
        {card.conversationStarter === null ? null : (
          <span className="block truncate text-xs text-brand">
            &ldquo;{card.conversationStarter}&rdquo;
          </span>
        )}
      </span>
    </Link>
  );
}
